import { useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { BEFORE_AFTER } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const CompareCard = ({ item }) => {
    const ref = useRef(null);
    const dragging = useRef(false);
    const [pos, setPos] = useState(50);

    const update = (clientX) => {
        const rect = ref.current.getBoundingClientRect();
        const next = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.min(96, Math.max(4, next)));
    };

    return (
        <figure data-testid={`beforeafter-card-${item.id}`}>
            <div
                ref={ref}
                data-testid={`beforeafter-slider-${item.id}`}
                className="relative aspect-[4/3] cursor-ew-resize touch-pan-y select-none overflow-hidden rounded-md border border-white/10"
                onPointerDown={(e) => {
                    dragging.current = true;
                    e.currentTarget.setPointerCapture(e.pointerId);
                    update(e.clientX);
                }}
                onPointerMove={(e) => {
                    if (dragging.current) update(e.clientX);
                }}
                onPointerUp={() => {
                    dragging.current = false;
                }}
                onPointerCancel={() => {
                    dragging.current = false;
                }}
            >
                <img
                    src={item.before}
                    alt={`Antes — ${item.title}`}
                    draggable={false}
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                />
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
                >
                    <img
                        src={item.after}
                        alt={`Después — ${item.title}`}
                        draggable={false}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                </div>

                <span className="pointer-events-none absolute left-3 top-3 rounded-sm bg-ink/70 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white backdrop-blur-sm">
                    Antes
                </span>
                <span className="pointer-events-none absolute right-3 top-3 rounded-sm bg-gold/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-ink backdrop-blur-sm">
                    Después
                </span>

                <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%` }}>
                    <div className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white/90" />
                    <div className="absolute top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-ink/70 shadow-lg shadow-black/40 backdrop-blur-sm">
                        <ChevronsLeftRight className="h-5 w-5 text-white" />
                    </div>
                </div>
            </div>
            <figcaption className="mt-4 text-sm font-semibold text-gray-300">
                {item.title}
            </figcaption>
        </figure>
    );
};

export const BeforeAfter = () => (
    <section
        id="galeria"
        data-testid="beforeafter-section"
        className="texture-carbon scroll-mt-20 border-y border-white/10 py-24"
    >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
                <p className="gold-text text-xs font-bold uppercase tracking-[0.3em]">
                    04 · Resultados que hablan solos
                </p>
                <h2 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-[#F3F4F6] sm:text-5xl">
                    Antes y <span className="gold-text">Después</span>
                </h2>
                <p className="mt-4 max-w-2xl text-base text-gray-400">
                    Desliza el control sobre cada imagen para comparar el resultado de cada
                    tratamiento.
                </p>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
                {BEFORE_AFTER.map((item, index) => (
                    <Reveal key={item.id} delay={index * 0.08}>
                        <CompareCard item={item} />
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
